import { Card, CardContent, CardHeader, SvgIcon } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import {
  RiCoupon2Line,
  RiMoneyEuroCircleLine,
  RiShieldUserLine,
  RiShipLine,
} from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import CustomDataGrid from '../../../components/CustomDataGrid';
import { renderSkeletonCards } from '../../../components/SkeletonElements';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useContractSelectOptions from '../../../hooks/useContractSelectOptions';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import {
  DAYJS_VISUALIZATION_DATE_FORMAT,
  DAYJS_VISUALIZATION_TIME_FORMAT,
  MONTHS,
} from '../../../utils/date';
import { DashboardCard } from './DashboardCards';
import DashboardPageWrapper from './DashboardPageWrapper';
import { InvoiceStatus } from '../../../types/invoices';
import { CrewMemberStatus, VesselStatus } from '../../../types/core';

const YEAR_MONTH_SEPARATOR = '-';
const DATA_LABEL_SEPARATOR = ',';
const ITEMS_SEPARATOR = ';';

interface PendingInvoiceElement {
  id: string;
  contractName: string;
  pendingInvoicesCount: number;
  expectedInvoicesCount: number;
  missingMonths: string /* Format YYYY-MM,YYYY-MM,YYYY-MM... */;
}

const AdminDashboard = () => {
  const { t } = useLanguage();
  const { user } = useCurrentUser();
  const {
    getAdminDashboardInfo,
    getAdminDashboardPendingInvoiceInfo,
    getContracts,
  } = useIotsApi();

  const {
    data: dashboardInfo = null,
    error: dashboardInfoError,
    isLoading: isLoadingDashboardInfo,
    isFetching: isFetchingDashboardInfo,
  } = useQuery({
    queryKey: ['dashboard', user.id],
    queryFn: () => getAdminDashboardInfo(),
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });

  const {
    data: fetchedPendingInvoices = null,
    error: pendingInvoicesError,
    isLoading: isLoadingPendingInvoices,
    isFetching: isFetchingPendingInvoices,
  } = useQuery({
    queryKey: ['pending-invoices'],
    queryFn: () => getAdminDashboardPendingInvoiceInfo(),
    refetchOnMount: 'always',
  });

  const {
    data: contracts = [],
    error: contractsError,
    isLoading: isLoadingContracts,
    isFetching: isFetchingContracts,
  } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => getContracts(),
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useQueryErrorNotifier([
    dashboardInfoError,
    pendingInvoicesError,
    contractsError,
  ]);

  const { contractsMap } = useContractSelectOptions(contracts, null);

  const pendingInvoices = useMemo(() => {
    const pendingInvoices: PendingInvoiceElement[] = [];

    if (isLoadingContracts || isLoadingPendingInvoices) {
      return pendingInvoices;
    }

    return fetchedPendingInvoices?.invoices
      .filter(({ invoicePending }) => invoicePending > 0)
      .map<PendingInvoiceElement>(
        ({ contractId, invoicePending, expectedInvoices, missedInvoices }) => ({
          id: contractId,
          contractName: contractsMap[contractId].label,
          pendingInvoicesCount: invoicePending,
          expectedInvoicesCount: expectedInvoices,
          missingMonths: missedInvoices
            .map(({ month, year }) =>
              [
                [year, month].join(YEAR_MONTH_SEPARATOR),
                `${t(`months.${MONTHS[month - 1]}`)} ${year}`,
              ].join(DATA_LABEL_SEPARATOR),
            )
            .sort((a, b) => a.localeCompare(b))
            .join(ITEMS_SEPARATOR),
        }),
      );
  }, [
    isLoadingContracts,
    isLoadingPendingInvoices,
    fetchedPendingInvoices,
    contractsMap,
    t,
  ]);

  const pendingAdhocJourneysList = useMemo<string[]>(() => {
    if (isLoadingDashboardInfo || isLoadingContracts) {
      return [];
    }

    if (!dashboardInfo || !dashboardInfo.adHocJourneys.toolTip) {
      return [];
    }

    const dataList = dashboardInfo.adHocJourneys.toolTip.reduce(
      (acc, item) => {
        const contractName =
          contractsMap[item.contractId]?.label ?? t('content.unknown');
        const newItems = item.periods.map<{
          contractName: string;
          year: number;
          month: number;
          text: string;
        }>(period => ({
          contractName,
          month: period.month,
          year: period.year,
          text: `${contractName} - ${t(`months.${MONTHS[period.month - 1]}`)} ${period.year}  (${period.pendingForApproval})`,
        }));
        acc.push(...newItems);
        return acc;
      },
      [] as Array<{
        contractName: string;
        year: number;
        month: number;
        text: string;
      }>,
    );

    return dataList
      .sort(
        (a, b) =>
          a.contractName.localeCompare(b.contractName) ||
          a.year - b.year ||
          a.month - b.month,
      )
      .map(dataItem => dataItem.text);
  }, [
    dashboardInfo,
    isLoadingDashboardInfo,
    isLoadingContracts,
    contractsMap,
    t,
  ]);

  const columns = useMemo<GridColDef<PendingInvoiceElement>[]>(
    () => [
      {
        field: 'contractName',
        flex: 3,
        headerName: t('content.contract'),
        minWidth: 160,
      },
      {
        field: 'pendingInvoicesCount',
        flex: 1,
        headerName: t('content.missing-invoices'),
        minWidth: 160,
      },
      {
        field: 'missingMonths',
        flex: 3,
        minWidth: 200,
        headerName: t('content.uncompleted-months'),
        valueFormatter: (missingMonthsText: string) =>
          missingMonthsText
            .split(ITEMS_SEPARATOR)
            .map(dataLabel => dataLabel.split(DATA_LABEL_SEPARATOR)[1])
            .join(', '),
      },
    ],
    [t],
  );

  const totalPendingTasks = !dashboardInfo
    ? 0
    : dashboardInfo.invoice.pendingForApproval +
      dashboardInfo.skipper.pendingForApproval +
      dashboardInfo.vessel.pendingForApproval +
      dashboardInfo.adHocJourneys.pendingForApproval;

  const lastUpdateDate = dayjs(fetchedPendingInvoices?.lastUpdateDate);
  return (
    <DashboardPageWrapper
      title={`${t('content.welcome')}, ${user.firstName}`}
      subtitle={
        dashboardInfo
          ? `${t('content.you-have-pending-tasks', {
              PENDING_TASKS: totalPendingTasks.toString(),
            })}`
          : null
      }
      isLoading={
        isFetchingDashboardInfo ||
        isFetchingPendingInvoices ||
        isFetchingContracts
      }
    >
      {isLoadingDashboardInfo ||
      isLoadingPendingInvoices ||
      isLoadingContracts ? (
        <div className="flex justify-center items-center min-h-[60vh]">
          {renderSkeletonCards(3)}
        </div>
      ) : !dashboardInfo ? (
        <div className="flex items-center justify-center min-h-[60vh]">
          <h1 className="text-4xl font-bold">
            {t('content.no-admin-dashboard-data')}
          </h1>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <DashboardCard
              icon={<SvgIcon component={RiShieldUserLine} />}
              title={t(`content.skippers`)}
              items={[
                {
                  label: t('content.awaiting-approval-cert'),
                  amount: dashboardInfo.skipper.pendingForApproval,
                  warning: !!dashboardInfo.skipper.pendingForApproval,
                },
                {
                  label: t('content.uncompliant'),
                  amount: dashboardInfo.skipper.uncompliant,
                  warning: false,
                },
              ].filter(item => item.amount > 0)}
              actionUrl="/admin/crew"
              actionUrlFilters={
                dashboardInfo.skipper.pendingForApproval > 0
                  ? [
                      {
                        field: 'pendingCertificationsCount',
                        operator: '>',
                        value: 0,
                      },
                    ]
                  : dashboardInfo.skipper.uncompliant > 0
                    ? [
                        {
                          field: 'status',
                          operator: 'is',
                          value: CrewMemberStatus.Uncompliant,
                        },
                      ]
                    : []
              }
            />
            <DashboardCard
              icon={<SvgIcon component={RiShipLine} />}
              title={t(`content.vessels`)}
              items={[
                {
                  label: t('content.awaiting-approval-cert'),
                  amount: dashboardInfo.vessel.pendingForApproval,
                  warning: !!dashboardInfo.vessel.pendingForApproval,
                },
                {
                  label: t('content.uncompliant'),
                  amount: dashboardInfo.vessel.uncompliant,
                  warning: false,
                },
              ].filter(item => item.amount > 0)}
              actionUrl="/admin/vessels"
              actionUrlFilters={
                dashboardInfo.vessel.pendingForApproval > 0
                  ? [
                      {
                        field: 'pendingCertificationsCount',
                        operator: '>',
                        value: 0,
                      },
                    ]
                  : dashboardInfo.vessel.uncompliant > 0
                    ? [
                        {
                          field: 'status',
                          operator: 'is',
                          value: VesselStatus.Uncompliant,
                        },
                      ]
                    : []
              }
            />
            <DashboardCard
              icon={<SvgIcon component={RiCoupon2Line} />}
              title={t(`content.ad-hoc-journeys`)}
              lastUpdate={dayjs(
                dashboardInfo.adHocJourneys.lastCalculationUpdate,
              )}
              items={[
                {
                  label: t('content.awaiting-approval'),
                  amount: dashboardInfo.adHocJourneys.pendingForApproval,
                  warning: dashboardInfo.adHocJourneys.pendingForApproval > 0,
                  helperContent: pendingAdhocJourneysList.length ? (
                    <ul>
                      {pendingAdhocJourneysList.map(text => (
                        <li key={text}>{text}</li>
                      ))}
                    </ul>
                  ) : undefined,
                },
              ].filter(item => item.amount > 0)}
              actionUrl="/admin/journeys"
            />
            <DashboardCard
              icon={<SvgIcon component={RiMoneyEuroCircleLine} />}
              title={t(`content.invoices`)}
              items={[
                {
                  label: t('content.awaiting-approval'),
                  amount: dashboardInfo.invoice.pendingForApproval,
                  warning: dashboardInfo.invoice.pendingForApproval > 0,
                },
              ].filter(item => item.amount > 0)}
              actionUrl="/admin/invoices"
              actionUrlFilters={
                dashboardInfo.invoice.pendingForApproval > 0
                  ? [
                      {
                        field: 'status',
                        operator: 'is',
                        value: InvoiceStatus.PendingApproval,
                      },
                    ]
                  : []
              }
            />
          </div>
          <Card>
            <CardHeader
              title={
                <div className="flex justify-between items-center w-full">
                  <span>
                    {t('content.contracts-with-missing-invoices')} (
                    {pendingInvoices?.length})
                  </span>
                  <span className="text-white text-xs text-right w-min">
                    <span className="whitespace-nowrap">
                      {t('content.last-update')}:
                    </span>{' '}
                    <span className="whitespace-nowrap">{`${lastUpdateDate.format(DAYJS_VISUALIZATION_DATE_FORMAT)}`}</span>{' '}
                    <span className="whitespace-nowrap">{`${lastUpdateDate.format(DAYJS_VISUALIZATION_TIME_FORMAT)}`}</span>
                  </span>
                </div>
              }
              className="border-b"
            />
            <CardContent>
              <CustomDataGrid
                rows={pendingInvoices}
                loading={isFetchingPendingInvoices}
                columns={columns}
                sx={{
                  // This specific table will have pseudo elements with shadows so they work as borders on the sides without
                  // affecting the actual sizing of the table
                  '& .MuiDataGrid-main::after, & .MuiDataGrid-main::before': {
                    content: "''",
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: '1px',
                    zIndex: 1,
                    boxShadow: "#0002 0 0 0 1px"
                  },
                  '& .MuiDataGrid-main::before': {
                    left: -1,
                  },
                  '& .MuiDataGrid-main::after': {
                    right: -1,
                  },
                }}
                initialState={{
                  sorting: {
                    sortModel: [
                      {
                        field: 'contractName',
                        sort: 'asc',
                      },
                    ],
                  },
                }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </DashboardPageWrapper>
  );
};

export default AdminDashboard;
